import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { StripeCoupon, StripeSubscriptionInterval, StripeSubscriptionStatus } from './schemas'
import { OrganizationDirectoryUserRead } from '@/organizations/schemas'

export interface AccountState {
  initialLoadComplete: boolean
  lastRefresh: number | null
  loading: boolean
  error: boolean
  stripeSubscriptionStatus: StripeSubscriptionStatus | null
  trialEnd: number | null
  cancelAtPeriodEnd: boolean
  stripeSubscriptionInterval: StripeSubscriptionInterval | null
  upcomingInvoiceAmountDueInCents: number | null
  numberOfSeats: number | null
  costPerSeatInCents: number | null
  hasDiscount: boolean
  appliedCoupon: StripeCoupon | null
  hasLegacyStudentPlan: boolean
  orgDirectoryUsers: OrganizationDirectoryUserRead[]
}

const initialState: AccountState = {
  initialLoadComplete: false,
  lastRefresh: null,
  loading: false,
  error: false,
  stripeSubscriptionStatus: null,
  trialEnd: null,
  cancelAtPeriodEnd: false,
  stripeSubscriptionInterval: null,
  upcomingInvoiceAmountDueInCents: null,
  numberOfSeats: null,
  costPerSeatInCents: null,
  hasDiscount: false,
  appliedCoupon: null,
  hasLegacyStudentPlan: false,
  orgDirectoryUsers: [],
}

/**
 * Account Slice
 *
 * Stores subscription and billing-related data for authenticated users
 * who already have an active or trialing Stripe subscription.
 *
 * Note: This is distinct from `checkoutSlice`, which handles pricing and plan selection
 * for users in the process of subscribing.
 */
export const accountSlice = createSlice({
  name: 'accountState',
  initialState,
  reducers: {
    // ============== Listener Middleware Triggers ============== >
    refreshPaymentDetails: (_state, _action: PayloadAction<{ forceRefresh: boolean }>) => {
      // Handled by listener middleware
    },
    refreshOrgDirectoryUsers: () => {
      // Handled by listener middleware
    },
    // ============== Slice Actions ============== >
    nullifyData: () => initialState,

    // If the data has loaded and there is data available
    setInitialLoadComplete: (state, action: PayloadAction<boolean>) => {
      state.initialLoadComplete = action.payload
    },

    // When the data was last refreshed
    setLastRefresh: (state, action: PayloadAction<number | null>) => {
      state.lastRefresh = action.payload
    },

    // If the data is currently being fetched
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },

    // If there is an error fetching the data
    setError: (state, action: PayloadAction<boolean>) => {
      state.error = action.payload
    },

    // Stripe subscription status
    setStripeSubscriptionStatus: (state, action: PayloadAction<StripeSubscriptionStatus | null>) => {
      state.stripeSubscriptionStatus = action.payload
    },

    // Trial end date in ms
    setTrialEnd: (state, action: PayloadAction<number | null>) => {
      state.trialEnd = action.payload
    },

    // If the subscription is set to be canceled at the end of the current period
    setCancelAtPeriodEnd: (state, action: PayloadAction<boolean>) => {
      state.cancelAtPeriodEnd = action.payload
    },

    // Stripe subscription interval (month or year)
    setStripeSubscriptionInterval: (state, action: PayloadAction<StripeSubscriptionInterval | null>) => {
      state.stripeSubscriptionInterval = action.payload
    },

    // Amount that will be charged for the upcoming invoice
    setUpcomingInvoiceAmountDueInCents: (state, action: PayloadAction<number | null>) => {
      state.upcomingInvoiceAmountDueInCents = action.payload
    },

    // If there is a discount applied
    setDiscountInfo: (state, action: PayloadAction<{ hasDiscount: boolean; appliedCoupon: StripeCoupon | null }>) => {
      state.hasDiscount = action.payload.hasDiscount
      state.appliedCoupon = action.payload.appliedCoupon
    },

    // The quantity of the subscription (number of seats) and unit cost (cost per seat)
    setSeatQuantityAndRate: (state, action: PayloadAction<{ numberOfSeats: number | null; costPerSeatInCents: number | null }>) => {
      state.numberOfSeats = action.payload.numberOfSeats
      state.costPerSeatInCents = action.payload.costPerSeatInCents
    },

    // If the user has a legacy student plan
    setHasLegacyStudentPlan: (state, action: PayloadAction<boolean>) => {
      state.hasLegacyStudentPlan = action.payload
    },

    setOrgDirectoryUsers: (state, action: PayloadAction<OrganizationDirectoryUserRead[]>) => {
      state.orgDirectoryUsers = action.payload
    },
  },
})

export const AccountActions = accountSlice.actions

export default accountSlice.reducer
